import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import { colors } from "src/styles";
import styled from "styled-components";
import { getPmDashboardData } from "src/pages/PreventativeMaintenance/data";

const serviceColor = (pct: number): string => {
  if (pct >= 85) return colors.kleenway.green;
  if (pct >= 75) return "#BA7517";
  return "#E24B4A";
};

// ─── Sub-component: single ring chart ────────────────────────────
const RingChart = ({
  pct,
  color,
  size = 72,
  strokeWidth = 7,
}: {
  pct: number;
  color: string;
  size?: number;
  strokeWidth?: number;
}) => {
  const circleRef = useRef<SVGCircleElement>(null);
  const r = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * r;

  useEffect(() => {
    if (!circleRef.current) return;
    const offset = circumference * (1 - pct / 100);
    circleRef.current.style.transition = "stroke-dashoffset 1s ease";
    circleRef.current.style.strokeDashoffset = String(offset);
  }, [pct, circumference]);

  return (
    <svg width={size} height={size} viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`}>
      <circle r={r} fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth={strokeWidth} />
      <circle
        ref={circleRef}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        transform="rotate(-90)"
      />
    </svg>
  );
};

// ─── Exported: stacked Month + YTD compliance tiles ──────────────
export const PmComplianceTiles = () => {
  const { clientId } = useSelector(getUserData);

  const { month, ytd } = getPmDashboardData(clientId);
  const monthPct = Math.round((month.completed / month.total) * 100);
  const ytdPct = Math.round((ytd.completed / ytd.total) * 100);

  return (
    <TilesCol>
      <ComplianceCard>
        <ComplianceHeader>
          <MetricIconWrap $tone="blue">
            <ShieldGlyph />
          </MetricIconWrap>
          <MetricTitleWrap>
            <CardTitle>PM Services</CardTitle>
            <MetricPeriod>{month.label}</MetricPeriod>
          </MetricTitleWrap>
        </ComplianceHeader>
        <RingRow>
          <RingLegend>
            <LegendMuted>Compliance rate</LegendMuted>
            <LegendValue>{month.total.toLocaleString()}</LegendValue>
            <LegendSub>{month.completed} completed this month</LegendSub>
          </RingLegend>
          <RingWrap>
            <RingChart pct={monthPct} color="#2563eb" />
            <RingLabel>{monthPct}%</RingLabel>
          </RingWrap>
        </RingRow>
      </ComplianceCard>

      <ComplianceCard>
        <ComplianceHeader>
          <MetricIconWrap $tone="green">
            <BuildingGlyph>
              <span />
              <span />
              <span />
            </BuildingGlyph>
          </MetricIconWrap>
          <MetricTitleWrap>
            <CardTitle>Completed (YTD)</CardTitle>
            <MetricPeriod>{ytd.label}</MetricPeriod>
          </MetricTitleWrap>
        </ComplianceHeader>
        <RingRow>
          <RingLegend>
            <LegendMuted>Compliance rate</LegendMuted>
            <LegendValue $tone="green">{ytd.completed.toLocaleString()}</LegendValue>
            <LegendSub>{ytd.total.toLocaleString()} total required visits</LegendSub>
          </RingLegend>
          <RingWrap>
            <RingChart pct={ytdPct} color="#1cb97f" />
            <RingLabel>{ytdPct}%</RingLabel>
          </RingWrap>
        </RingRow>
      </ComplianceCard>
    </TilesCol>
  );
};

// ─── Exported: services compliance table ─────────────────────────
export const PmServicesTable = () => {
  const { clientId } = useSelector(getUserData);
  const { services } = getPmDashboardData(clientId);

  return (
    <WidgetContainer>
      <WidgetHeader>
        <div>
          <CardTitle>Services</CardTitle>
          <WidgetSub>Compliance % (YTD)</WidgetSub>
        </div>
      </WidgetHeader>
      <ServiceList>
        {services.map((svc) => {
          const color = serviceColor(svc.compliancePct);
          return (
            <ServiceRow key={svc.serviceName}>
              <ServiceInfo>
                <ServiceName>{svc.serviceName}</ServiceName>
                <ServiceMeta>
                  {svc.compliancePct >= 85 ? "On Track" : svc.compliancePct >= 75 ? "Needs Attention" : "At Risk"}
                </ServiceMeta>
              </ServiceInfo>
              <BarWrap>
                <BarTrack>
                  <BarFill $pct={svc.compliancePct} $color={color} />
                </BarTrack>
                <ServicePct style={{ color }}>{svc.compliancePct}%</ServicePct>
              </BarWrap>
            </ServiceRow>
          );
        })}
      </ServiceList>
    </WidgetContainer>
  );
};

// ─── Default export: convenience wrapper (optional) ───────────────
const PreventativeMaintenancePanel = () => (
  <>
    <PmComplianceTiles />
    <PmServicesTable />
  </>
);

export default PreventativeMaintenancePanel;

// ─── Styled Components ────────────────────────────────────────────

const TilesCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;

  & > * {
    flex: 1;
  }
`;

const WidgetContainer = styled.div`
  background: #ffffff;
  border: 1px solid ${colors.grey.light10};
  border-radius: 20px;
  padding: 18px 20px;
  height: 300px;
  overflow-y: auto;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.04);
`;

const ComplianceCard = styled.div`
  background: #ffffff;
  border: 1px solid ${colors.grey.light10};
  border-radius: 20px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.04);
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.grey.dark2};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ComplianceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const MetricIconWrap = styled.div<{ $tone: "blue" | "green" }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $tone }) => ($tone === "blue" ? "#eaf1ff" : "#e6f8ef")};
`;

const ShieldGlyph = styled.div`
  width: 16px;
  height: 18px;
  border: 2px solid #2563eb;
  border-radius: 7px 7px 10px 10px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 50%;
    width: 4px;
    height: 7px;
    border-right: 2px solid #2563eb;
    border-bottom: 2px solid #2563eb;
    transform: translateX(-50%) rotate(45deg);
  }
`;

const BuildingGlyph = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  span {
    display: block;
    width: 16px;
    height: 3px;
    border-radius: 999px;
    background: #1cb97f;
  }
`;

const MetricTitleWrap = styled.div`
  min-width: 0;
`;

const MetricPeriod = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: ${colors.grey.dark};
`;

const RingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const RingWrap = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
`;

const RingLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${colors.grey.dark};
`;

const RingLegend = styled.div`
  flex: 1;
`;

const LegendMuted = styled.div`
  font-size: 11px;
  color: ${colors.grey.dark2};
`;

const LegendValue = styled.div<{ $tone?: "green" }>`
  font-size: 34px;
  font-weight: 700;
  line-height: 1.05;
  color: ${({ $tone }) => ($tone === "green" ? "#1cb97f" : colors.black.dark1)};
`;

const LegendSub = styled.div`
  font-size: 12px;
  color: ${colors.grey.dark};
  margin-top: 4px;
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const WidgetSub = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: ${colors.grey.dark};
`;

const ServiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${colors.grey.light10};
  border-radius: 14px;
  background: #fafbfd;
`;

const ServiceInfo = styled.div`
  min-width: 0;
`;

const ServiceName = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.black.dark1};
`;

const ServiceMeta = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: ${colors.grey.dark};
`;

const BarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 168px;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 7px;
  background: #e7eef5;
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 999px;
  transition: width 0.9s ease;
`;

const ServicePct = styled.span`
  font-size: 12px;
  font-weight: 700;
  min-width: 38px;
  text-align: right;
`;
